/* eslint-disable no-await-in-loop */
import moment from 'moment';

export const mapEditedRows = (form, currentPage, editingIds) => {
  const formValues = form.getFieldsValue();

  const validFormValues = (formValues.list || []).filter((item) => item && item.id !== undefined);

  const editedRowsData = validFormValues
    .map((editedValues) => {
      const originalTask = currentPage.find((task) => task.id === editedValues.id);

      if (originalTask) {
        const originalCustomFields = originalTask.customFields ?? originalTask.CustomFields ?? {};
        const editedCustomFields = editedValues.customFields ?? {};
        const fullData = {
          ...originalTask,
          ...editedValues,
          customFields: { ...originalCustomFields, ...editedCustomFields },
        };
        return fullData;
      }
      return null;
    })
    .filter((item) => item && editingIds.includes(item.id));

  return editedRowsData;
};

export const mapSubmitData = async (
  editedRowsData,
  invoiceIssuersOptions,
  instructorOptions,
  driverOptions,
  transportCompanyOptions,
  vehicleOptions,
  reasonOptions,
  stageOptions
) => {
  const waitingStage = stageOptions?.find(({ id }) => id === 1); // 1 = Aguardando
  const completedStage = stageOptions?.find(({ id }) => id === 5); // 5 = Concluído
  const submitData = [];

  for (let index = 0; index < editedRowsData.length; index += 1) {
    const item = editedRowsData[index];
    const itemData = JSON.parse(JSON.stringify(item));

    if (item.mainInstructor?.userId) {
      const mainInstructor = instructorOptions.find(({ id }) => id === item.mainInstructor.userId);

      itemData.mainInstructor = {
        userId: mainInstructor.id,
        userName: mainInstructor.name,
        stage: waitingStage, // Estágio = Aguardando
        acceptanceDeadline: new Date(moment(item.startDate).add(2, 'hours')),
      };
    }

    if (item.assistantInstructor?.userId) {
      const assistantInstructor = instructorOptions.find(
        ({ id }) => id === item.assistantInstructor.userId
      );

      itemData.assistantInstructor = {
        userId: assistantInstructor.id,
        userName: assistantInstructor.name,
        stage: waitingStage, // Estágio = Aguardando
        acceptanceDeadline: new Date(moment(item.startDate).add(2, 'hours')),
      };
    }

    if (item.transportCompany?.id) {
      const transportCompany = transportCompanyOptions.find(
        ({ id }) => id === item.transportCompany.id
      );

      itemData.transportCompany = {
        id: transportCompany.id,
        name: transportCompany.commercialName,
      };
    }

    if (item.reasonNoBilling?.id) {
      const reasonNoBilling = reasonOptions.find(({ id }) => id === item.reasonNoBilling.id);

      itemData.reasonNoBilling = reasonNoBilling
        ? { id: reasonNoBilling.id, name: reasonNoBilling.name }
        : { id: item.reasonNoBilling.id, name: item.reasonNoBilling.name };
    } else if ('reasonNoBilling' in item && !item.reasonNoBilling?.id) {
      itemData.reasonNoBilling = null;
    }

    if (item.invoiceIssuer?.id) {
      const invoiceIssuer = invoiceIssuersOptions.find(({ id }) => id === item.invoiceIssuer.id);

      itemData.invoiceIssuer = {
        id: invoiceIssuer.value,
        name: invoiceIssuer.label,
      };
    }

    if (item.driverList) {
      itemData.driverList = item.driverList.map((driver) => ({
        userId: driver.userId,
        userName: driver.userName,
        stage: waitingStage, // Estágio = Aguardando
        acceptanceDeadline: new Date(moment(item.startDate).add(2, 'hours')),
        vehicle: driver.vehicle || null, // Inclui o veículo vinculado se houver
      }));
    }

    if (item.hasDocumentation) {
      itemData.stage = completedStage; // Estágio = Concluído
    }

    submitData.push(itemData);
  }

  return submitData;
};

const formatValue = (value, inputType) => {
  if (!value) return value;

  switch (inputType) {
    case 'date':
      return moment(value);
    case 'dateTime':
      return moment(value);
    case 'time':
      return moment(value);
    default:
      return value;
  }
};

const INTEGER_DEAL_ID = /^-?\d+$/;

const isBlankFilter = (value) => value == null || String(value).trim() === '';

const matchesDealIdColumn = (task, dealIdFilter) => {
  if (isBlankFilter(dealIdFilter)) return true;
  const trimmed = String(dealIdFilter).trim();
  if (!INTEGER_DEAL_ID.test(trimmed)) return false;
  if (task.dealId == null || task.dealId === '') return false;
  return Number(task.dealId) === Number(trimmed);
};

const matchesDealClientColumn = (task, dealClientFilter) => {
  if (isBlankFilter(dealClientFilter)) return true;
  const reason = String(task.dealClient || '');
  if (!reason.trim()) return false;
  return reason.toLowerCase().includes(String(dealClientFilter).trim().toLowerCase());
};

const digitsOnly = (value) => String(value || '').replace(/\D/g, '');

const matchesCnpjColumn = (task, cnpjFilter) => {
  const typedDigits = digitsOnly(cnpjFilter);
  if (!typedDigits) return true;
  const storedDigits = digitsOnly(task.dealClientCNPJ);
  if (!storedDigits) return false;
  return storedDigits.includes(typedDigits);
};

/** Valores únicos da coluna Nome Cliente (`dealClient`) para o Autocomplete do painel. */
export const buildDealClientColumnOptions = (tasks) => {
  const seen = new Set();
  const names = [];
  (tasks || []).forEach((task) => {
    const name = String(task?.dealClient || '').trim();
    if (!name) return;
    const key = name.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    names.push(name);
  });
  names.sort((a, b) => a.localeCompare(b, 'pt-BR'));
  return names.map((value) => ({ value, label: value }));
};

/** Valores únicos da coluna CNPJ Cliente (`dealClientCNPJ`) para o Autocomplete do painel. */
export const buildDealClientCnpjColumnOptions = (tasks) => {
  const seen = new Set();
  const values = [];
  (tasks || []).forEach((task) => {
    const raw = String(task?.dealClientCNPJ || '').trim();
    if (!raw) return;
    const key = raw.replace(/\D/g, '');
    if (!key || seen.has(key)) return;
    seen.add(key);
    values.push(raw);
  });
  values.sort((a, b) => a.localeCompare(b, 'pt-BR'));
  return values.map((value) => ({ value, label: value }));
};

/** Restringe pela coluna Id Negócio / Nome Cliente / CNPJ Cliente (mesmo dado do painel). */
export const applyDedicatedTaskListFilters = (tasks, filters) => {
  if (!Array.isArray(tasks)) return tasks;
  return tasks.filter(
    (task) =>
      matchesDealIdColumn(task, filters?.dealId) &&
      matchesDealClientColumn(task, filters?.dealClient) &&
      matchesCnpjColumn(task, filters?.dealClientCNPJ)
  );
};

export const getCellInitialValue = (record, dataIndex, fieldName, inputType) => {
  let value = record;

  if (inputType === 'driverList') {
    value = record[dataIndex];

    if (Array.isArray(value)) {
      return value;
    }

    return [];
  }

  if (Array.isArray(fieldName) && fieldName[0] === 'customFields') {
    const customFields = record.customFields ?? record.CustomFields ?? {};
    return formatValue(customFields[fieldName[1]], inputType);
  }

  if (Array.isArray(fieldName) && fieldName.length > 1) {
    fieldName.forEach((field) => {
      if (value) {
        value = value[field];
      }
    });

    return formatValue(value, inputType);
  }

  value = record[dataIndex];
  return formatValue(value, inputType);
};
